import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, DollarSign, CreditCard, Plane } from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';
import PaymentMethodSelector from '../components/PaymentMethodSelector';

const RiderRequest = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 16),
    pickup_location: '',
    dropoff_location: '',
    miles_driven: '',
    time_hours: '',
    client_phone: '',
    client_email: '',
    preferred_driver_id: '',
    notes: '',
    is_airport_pickup: false,
    flight_number: '',
    airline: ''
  });
  const [drivers, setDrivers] = useState([]);
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [tipAmount, setTipAmount] = useState(0);
  const [customTip, setCustomTip] = useState('');

  // Common Airlines
  const AIRLINES = [
    'American Airlines',
    'Delta Air Lines', 
    'United Airlines',
    'Southwest Airlines',
    'JetBlue Airways',
    'Alaska Airlines',
    'Spirit Airlines',
    'Frontier Airlines',
    'Hawaiian Airlines',
    'Allegiant Air',
    'Sun Country Airlines',
    'Other'
  ];

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setDrivers(response.data);
    } catch (error) {
      console.error('Failed to load drivers');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      if ((name === 'pickup_location' || name === 'dropoff_location') && newData.pickup_location && newData.dropoff_location) {
        calculateDistanceAndTime(name === 'pickup_location' ? value : newData.pickup_location, name === 'dropoff_location' ? value : newData.dropoff_location);
      }
      return newData;
    });
  };

  const calculateDistanceAndTime = async (pickup, dropoff) => {
    if (!pickup || !dropoff || !window.google) return;
    try {
      const service = new window.google.maps.DistanceMatrixService();
      service.getDistanceMatrix({
        origins: [pickup],
        destinations: [dropoff],
        travelMode: 'DRIVING',
        unitSystem: window.google.maps.UnitSystem.IMPERIAL
      }, async (response, status) => {
        if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
          const element = response.rows[0].elements[0];
          const distanceText = element.distance.text;
          const miles = parseFloat(distanceText.replace(' mi', ''));
          const durationInSeconds = element.duration.value;
          const hours = (durationInSeconds / 3600).toFixed(2);
          setFormData(prev => ({ ...prev, miles_driven: miles.toString(), time_hours: hours.toString() }));
          await calculatePrice(miles, parseFloat(hours));
        }
      });
    } catch (error) {
      console.error('Error calculating distance:', error);
    }
  };

  const calculatePrice = async (miles, hours) => {
    try {
      const response = await axios.post(`${API}/calculate-price`, { distance_miles: miles, time_hours: hours });
      setPriceEstimate(response.data);
      toast.success(`Estimated Price: $${response.data.final_price}`, { duration: 3000 });
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();
    setShowPayment(true);
  };

  const handlePaymentSuccess = async (intentId) => {
    setPaymentIntentId(intentId);
    setLoading(true);
    try {
      const payload = {
        date: new Date(formData.date).toISOString(),
        pickup_location: formData.pickup_location,
        dropoff_location: formData.dropoff_location,
        miles_driven: parseFloat(formData.miles_driven),
        time_hours: parseFloat(formData.time_hours),
        client_phone: formData.client_phone,
        preferred_driver_id: formData.preferred_driver_id === 'none' ? null : formData.preferred_driver_id,
        notes: formData.notes,
        payment_intent_id: intentId,
        is_airport_pickup: formData.is_airport_pickup,
        flight_number: formData.is_airport_pickup ? formData.flight_number : null
      };
      const response = await axios.post(`${API}/ride-request`, payload);
      toast.success(`Ride request created successfully! Your ride has been booked.`);
      setTimeout(() => navigate('/rides'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ride request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="rider-request-page">
      <Button variant="ghost" onClick={() => navigate('/')} className="mb-4" data-testid="back-button">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Dashboard
      </Button>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Request a Ride</CardTitle>
          <p className="text-sm text-gray-600">Enter your ride details and get instant pricing</p>
        </CardHeader>
        <CardContent>
          <form className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Pickup Date & Time *</Label>
                <Input id="date" name="date" type="datetime-local" value={formData.date} onChange={handleChange} required className="form-input" data-testid="ride-date-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_phone">Your Phone Number</Label>
                <Input id="client_phone" name="client_phone" type="tel" placeholder="(555) 123-4567" value={formData.client_phone} onChange={handleChange} className="form-input" data-testid="rider-phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="client_email">Your Email *</Label>
              <Input id="client_email" name="client_email" type="email" placeholder="you@example.com" value={formData.client_email} onChange={handleChange} required className="form-input" data-testid="rider-email-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pickup_location">Pickup Location *</Label>
                <AddressAutocomplete id="pickup_location" name="pickup_location" placeholder="Start typing address..." value={formData.pickup_location} onChange={handleChange} required={true} dataTestId="rider-pickup-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dropoff_location">Dropoff Location *</Label>
                <AddressAutocomplete id="dropoff_location" name="dropoff_location" placeholder="Start typing address..." value={formData.dropoff_location} onChange={handleChange} required={true} dataTestId="rider-dropoff-input" />
              </div>
            </div>
            
            {/* Airport Pickup Section */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div className="flex items-center h-6">
                    <input
                      type="checkbox"
                      id="is_airport_pickup"
                      checked={formData.is_airport_pickup}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        is_airport_pickup: e.target.checked,
                        flight_number: e.target.checked ? prev.flight_number : ''
                      }))}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="is_airport_pickup" className="text-base font-semibold text-gray-900 cursor-pointer flex items-center gap-2">
                      <Plane className="w-5 h-5 text-blue-600" />
                      Airport Pickup
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      Check this if you&apos;re being picked up from the airport. We&apos;ll track your flight for delays.
                    </p>
                    
                    {formData.is_airport_pickup && (
                      <div className="mt-4 space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="airline" className="text-sm font-medium">
                            Airline *
                          </Label>
                          <Select
                            value={formData.airline}
                            onValueChange={(value) => setFormData(prev => ({ ...prev, airline: value }))}
                          >
                            <SelectTrigger className="bg-white">
                              <SelectValue placeholder="Select your airline" />
                            </SelectTrigger>
                            <SelectContent>
                              {AIRLINES.map(airline => (
                                <SelectItem key={airline} value={airline}>
                                  {airline}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="flight_number" className="text-sm font-medium">
                            Flight Number *
                          </Label>
                          <Input
                            id="flight_number"
                            name="flight_number"
                            placeholder="e.g., AA1234, UA567, DL890"
                            value={formData.flight_number}
                            onChange={handleChange}
                            className="bg-white uppercase"
                            required={formData.is_airport_pickup}
                          />
                          <p className="text-xs text-gray-500">
                            Enter your flight number so we can track arrivals and adjust pickup time if needed.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            {priceEstimate && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Estimated Price
                    </h3>
                    <div className="text-3xl font-bold text-blue-600">${priceEstimate.final_price}</div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Based on {formData.miles_driven} miles and {(formData.time_hours * 60).toFixed(0)} minutes</p>
                </CardContent>
              </Card>
            )}
            {priceEstimate && (
              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Add a Tip (Optional)</h3>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {[10, 15, 20, 25].map((percent) => {
                      const amount = (priceEstimate.final_price * percent / 100).toFixed(2);
                      return (
                        <Button
                          key={percent}
                          type="button"
                          variant={tipAmount === parseFloat(amount) ? "default" : "outline"}
                          onClick={() => {
                            setTipAmount(parseFloat(amount));
                            setCustomTip('');
                          }}
                          className="w-full"
                        >
                          {percent}%<br />${amount}
                        </Button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Custom tip amount"
                      value={customTip}
                      onChange={(e) => {
                        setCustomTip(e.target.value);
                        setTipAmount(parseFloat(e.target.value) || 0);
                      }}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  {tipAmount > 0 && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex justify-between items-center font-semibold text-green-900">
                        <span>Total (including tip):</span>
                        <span className="text-xl">${(priceEstimate.final_price + tipAmount).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            <div className="space-y-2">
              <Label htmlFor="preferred_driver_id">Preferred Driver (Optional)</Label>
              <Select value={formData.preferred_driver_id || "none"} onValueChange={(value) => setFormData(prev => ({ ...prev, preferred_driver_id: value === "none" ? "" : value }))}>
                <SelectTrigger data-testid="preferred-driver-select"><SelectValue placeholder="No preference - any available driver" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No preference - any available driver</SelectItem>
                  {drivers.map((driver) => (<SelectItem key={driver.id} value={driver.id}>{driver.name} ({driver.email})</SelectItem>))}
                </SelectContent>
              </Select>
              {formData.preferred_driver_id && formData.preferred_driver_id !== 'none' && (<p className="text-xs text-blue-600">✓ Your selected driver will get the full fare</p>)}
              {(!formData.preferred_driver_id || formData.preferred_driver_id === 'none') && priceEstimate && (<p className="text-xs text-gray-600">ℹ️ Your ride will go to the message board. Driver gets 80%, admin gets 20%</p>)}
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea id="notes" name="notes" placeholder="Any special instructions..." value={formData.notes} onChange={handleChange} rows={3} className="form-input" data-testid="rider-notes-input" />
            </div>
            
            {!showPayment ? (
              <Button 
                onClick={handleProceedToPayment} 
                className="w-full btn-primary" 
                disabled={loading || !priceEstimate} 
                data-testid="proceed-payment-button"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Proceed to Payment - ${tipAmount > 0 ? (priceEstimate?.final_price + tipAmount).toFixed(2) : priceEstimate?.final_price || '0.00'}
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Payment Details
                  </h3>
                  {tipAmount > 0 && (
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="space-y-2">
                        <div className="flex justify-between text-gray-700">
                          <span>Ride Fare:</span>
                          <span>${priceEstimate?.final_price.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-700">
                          <span>Tip:</span>
                          <span>${tipAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-lg text-blue-600 pt-2 border-t">
                          <span>Total:</span>
                          <span>${(priceEstimate?.final_price + tipAmount).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <PaymentMethodSelector
                    amount={tipAmount > 0 ? priceEstimate?.final_price + tipAmount : priceEstimate?.final_price || 0}
                    description={`Ride from ${formData.pickup_location} to ${formData.dropoff_location}${tipAmount > 0 ? ` (includes $${tipAmount.toFixed(2)} tip)` : ''}`}
                    rideId={null}
                    onSuccess={handlePaymentSuccess}
                    onError={(error) => {
                      console.error('Payment error:', error);
                      toast.error('Payment failed. Please try again.');
                    }}
                  />
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setShowPayment(false)} 
                  className="w-full"
                  type="button"
                >
                  Back to Ride Details
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RiderRequest;